<script lang="ts">
  /**
   * A project on the phone's read-only canvas (ma-inzp): emoji, title, status and
   * task progress. No task rows, menus or resize handles; tapping opens the
   * project rail. Like the editable card's summary mode, the content is
   * inverse-scaled so it stays readable when the canvas is zoomed out.
   */
  import type { NodeProps } from '@xyflow/svelte';
  import type { Project } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';
  import { getBoardDisplay } from '../../lib/boardContext';
  import { getTaskCounts, subscribeToProjectTaskCounts } from '../../lib/useProjectTaskCounts';
  import { openProjectRail } from '../../lib/stores/ui';

  type Props = NodeProps & { data: { project: Project } };

  let { data, width, height }: Props = $props();

  const display = getBoardDisplay();

  let project = $derived(data.project);
  // svelte-ignore state_referenced_locally
  let counts = $state(getTaskCounts(data.project));

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes on project change, returns cleanup
  $effect(() => subscribeToProjectTaskCounts(project, (next) => (counts = next)));

  let scale = $derived(Math.max(1, 1 / Math.max(display.zoom, 0.2)));
  let statusLabel = $derived(project.status.charAt(0).toUpperCase() + project.status.slice(1));
  let taskSummary = $derived(counts.total > 0 ? `${counts.done}/${counts.total}` : 'No tasks');
  let pct = $derived(counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : 0);
  let cardWidth = $derived(width ?? 260);
  let cardHeight = $derived(height ?? 256);
  // The content box (in unscaled px) only gets what fits: title lines first, then
  // the status line, so a small card is never cut mid-line.
  let boxHeight = $derived(cardHeight / scale);
  let showSub = $derived(boxHeight >= 64);
  let titleLines = $derived(
    Math.max(1, Math.min(4, Math.floor((boxHeight - 26 - (showSub ? 21 : 0)) / 20))),
  );
</script>

<button
  type="button"
  class="card"
  data-status={project.status}
  style="width: {cardWidth}px; height: {cardHeight}px;"
  onclick={() => openProjectRail(project.id)}
>
  <span
    class="content"
    style="width: {cardWidth / scale}px; height: {cardHeight / scale}px; transform: scale({scale});"
  >
    <span class="title" style="-webkit-line-clamp: {titleLines}; line-clamp: {titleLines};">
      <span aria-hidden="true">{categoryIcons[project.category] ?? '📋'}</span>
      {project.title}
    </span>
    {#if showSub}
      <span class="sub">{statusLabel} · {taskSummary}</span>
    {/if}
  </span>
  {#if counts.total > 0}
    <span class="bar" aria-hidden="true">
      <span class="fill" class:complete={pct === 100} style="width: {pct}%"></span>
    </span>
  {/if}
</button>

<style>
  .card {
    display: block;
    position: relative;
    padding: 0;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-top: 3px solid #9ca3af;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    box-sizing: border-box;
    font: inherit;
    text-align: left;
    color: #1f2937;
    cursor: pointer;
  }

  .card[data-status='planned'] {
    border-top-color: #a78bfa;
  }

  .card[data-status='active'],
  .card[data-status='almost'] {
    border-top-color: #34d399;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 6px;
    box-sizing: border-box;
    padding: 10px;
    transform-origin: top left;
  }

  .title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .sub {
    flex: none;
    font-size: 12px;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
  }

  /* A strip along the card's bottom edge, outside the scaled content, so it is
     never cut off however small the card is. */
  .bar {
    position: absolute;
    inset: auto 0 0;
    height: 4px;
    background: #e5e7eb;
  }

  .fill {
    display: block;
    height: 100%;
    background: #3b82f6;
  }

  .fill.complete {
    background: #22c55e;
  }
</style>
